import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, Loader2, XCircle } from "lucide-react";

import { authFetch } from "../context/AuthContext";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      console.log("Checking payment status for session:", sessionId);
      const response = await authFetch(
        `${API_URL}/api/subscription/checkout/status/${sessionId}`,
        {  }
      );

      console.log("Response status:", response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log("Payment data:", data);
        setPaymentInfo(data);
        if (data.payment_status === "paid") {
          setStatus("success");
        } else {
          setStatus("pending");
        }
      } else {
        // Try to get error details
        let errorDetail = `Status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorDetail = errorData.detail || errorData.message || errorDetail;
          console.error("Error response:", errorData);
        } catch (e) {
          console.error("Could not parse error response");
        }
        console.error("Payment verification failed:", errorDetail);
        setStatus("error");
      }
    } catch (error) {
      console.error("Status check failed:", error);
      setStatus("error");
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="subscription-success-page">
        <Card className="border border-border shadow-none max-w-md w-full">
          <CardContent className="p-8 text-center">
            {status === "loading" && (
              <>
                <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" />
                <h2 className="font-heading text-2xl text-primary mb-2">
                  Processing Payment...
                </h2>
                <p className="text-muted-foreground">
                  Please wait while we confirm your subscription.
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                <h2 className="font-heading text-2xl text-primary mb-2">
                  Welcome to Premium!
                </h2>
                <p className="text-muted-foreground mb-6">
                  Your subscription is now active. Enjoy unlimited access to all features!
                </p>
                {paymentInfo && (
                  <div className="bg-sand-50 rounded-sm p-4 mb-6 text-sm">
                    <p className="text-muted-foreground">Amount paid</p>
                    <p className="font-semibold text-lg">
                      ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                    </p>
                  </div>
                )}
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate("/wardrobe")}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-12"
                    data-testid="go-to-wardrobe-btn"
                  >
                    Start Adding Items
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/reports")}
                    className="w-full rounded-sm h-12"
                  >
                    Generate Your First Report
                  </Button>
                </div>
              </>
            )}

            {status === "pending" && (
              <>
                <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-amber-500" />
                <h2 className="font-heading text-2xl text-primary mb-2">
                  Payment Processing
                </h2>
                <p className="text-muted-foreground mb-6">
                  Your payment is being processed. This may take a moment.
                </p>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="rounded-sm"
                >
                  Check Status
                </Button>
              </>
            )}

            {status === "error" && (
              <>
                <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
                <h2 className="font-heading text-2xl text-primary mb-2">
                  Something Went Wrong
                </h2>
                <p className="text-muted-foreground mb-6">
                  We couldn't verify your payment. Please try again or contact support.
                </p>
                <Button
                  onClick={() => navigate("/subscription")}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                >
                  Back to Subscription
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
